import React, { useState, useEffect } from 'react';
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Toolbar, AppBar, Typography, IconButton, Divider, Avatar,
  Tooltip, useMediaQuery, useTheme, Chip, Menu, MenuItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BusinessIcon from '@mui/icons-material/Business';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import LoginIcon from '@mui/icons-material/Login';
import PeopleIcon from '@mui/icons-material/People';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate, useLocation } from 'react-router-dom';

const DNS_STATUS = process.env.REACT_APP_DNS_STATUS || 'manual-required';

const DRAWER_WIDTH = 240;

const NAV = [
  { label: 'Dashboard',   icon: <DashboardIcon />,     path: '/dashboard' },
  { label: 'Tenants',     icon: <BusinessIcon />,       path: '/tenants' },
  { label: 'Monitoring',  icon: <MonitorHeartIcon />,   path: '/monitoring' },
];

const SETTINGS_NAV = [
  { label: 'Trial Settings',   icon: <TimerOutlinedIcon />, path: '/settings/trial' },
  { label: 'Features & Add-Ons', icon: <TuneIcon />,        path: '/settings/feature-limits' },
  { label: 'Social Login',     icon: <LoginIcon />,         path: '/settings/social-providers' },
  { label: 'Sales',            icon: <StorefrontIcon />,    path: '/settings/sales' },
  { label: 'Advanced',         icon: <TuneOutlinedIcon />,  path: '/settings/advanced' },
  {
    label: 'DNS Setup',
    icon: <DnsOutlinedIcon />,
    path: '/platform/dns',
    badge: DNS_STATUS !== 'auto-configured' ? 'manual' : null,
  },
];

export default function AdminLayout({ children, onSignOut, user }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(!isMobile);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [userRole, setUserRole] = useState('admin');
  const navigate = useNavigate();
  const location = useLocation();

  // Extract role from JWT claims
  useEffect(() => {
    (async () => {
      try {
        const { fetchAuthSession } = await import('aws-amplify/auth');
        const session = await fetchAuthSession();
        const payload = session.tokens?.idToken?.payload;
        const role = payload?.['custom:role'] || 'platform_admin';
        setUserRole(role);
      } catch { /* keep default */ }
    })();
  }, []);

  const isManager = userRole === 'platform_manager';
  const roleLabel = isManager ? 'Manager' : 'Admin';

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ gap: 1 }}>
        <AdminPanelSettingsIcon color="primary" />
        <Box>
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>Admin Portal</Typography>
          <Typography variant="caption" color="text.secondary">Inventory Platform</Typography>
        </Box>
      </Toolbar>
      <Divider />
      <List sx={{ flex: 1, pt: 1 }}>
        {NAV.map((item) => (
          <ListItemButton
            key={item.path}
            selected={location.pathname.startsWith(item.path)}
            onClick={() => { navigate(item.path); if (isMobile) { setOpen(false); } }}
            sx={{ borderRadius: 1, mx: 1, mb: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14 }} />
          </ListItemButton>
        ))}
        {!isManager && (
          <>
            <ListItemButton
              selected={location.pathname === '/admin-users'}
              onClick={() => { navigate('/admin-users'); if (isMobile) { setOpen(false); } }}
              sx={{ borderRadius: 1, mx: 1, mb: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}><PeopleIcon /></ListItemIcon>
              <ListItemText primary="Users" primaryTypographyProps={{ fontSize: 14 }} />
            </ListItemButton>
            <Divider sx={{ mx: 2, my: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, pb: 0.5, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Platform
            </Typography>
            {SETTINGS_NAV.map((item) => (
              <ListItemButton
                key={item.path}
                selected={location.pathname.startsWith(item.path)}
                onClick={() => { navigate(item.path); if (isMobile) { setOpen(false); } }}
                sx={{ borderRadius: 1, mx: 1, mb: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14 }} />
                {item.badge && (
                  <Chip label={item.badge} size="small" color="warning" sx={{ fontSize: 9, height: 16, ml: 0.5 }} />
                )}
              </ListItemButton>
            ))}
          </>
        )}
      </List>
      <Divider />
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: 'primary.main' }}>
          {user?.signInDetails?.loginId?.[0]?.toUpperCase() || 'A'}
        </Avatar>
        <Box flex={1} minWidth={0}>
          <Typography variant="caption" noWrap display="block" fontWeight={500}>
            {user?.signInDetails?.loginId}
          </Typography>
          <Chip label={roleLabel} size="small" color={isManager ? 'warning' : 'primary'} sx={{ fontSize: 9, height: 16 }} />
        </Box>
        <Tooltip title="More options">
          <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <MenuItem onClick={() => { setMenuAnchor(null); navigate('/profile'); }}>
            <PersonIcon fontSize="small" sx={{ mr: 1 }} /> Profile
          </MenuItem>
        </Menu>
        <Tooltip title="Sign out">
          <IconButton size="small" onClick={onSignOut}><LogoutIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ display: { md: 'none' }, zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={() => setOpen((o) => !o)}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 1 }}>Admin Portal</Typography>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? open : true}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ width: DRAWER_WIDTH, flexShrink: 0, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' } }}
      >
        {drawer}
      </Drawer>

      <Box component="main" sx={{ flex: 1, minWidth: 0, mt: { xs: 8, md: 0 }, bgcolor: 'background.default', minHeight: '100vh' }}>
        {children}
      </Box>
    </Box>
  );
}
